

<template>
  <layout :drawer="false">
    <template slot="nav-tools">
      <smart-btn @click.native="onNewAgent" class="mr-1">
        <v-icon>mdi-account-plus</v-icon>
        <span class="pl-1">Nouvel agent sur place</span>
      </smart-btn>
    </template>

    <template slot="content">
      <template v-if="fetchData.noFetchYet">
        <v-container fluid class="pa-3">
          <v-row class="fill-height">
            <v-col cols="12">
              <v-skeleton-loader
                  class
                  height="100%"
                  type="list-item-avatar-two-line, list-item-three-line@2, list-item-avatar-two-line, list-item-three-line@3"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template v-else>
        <v-container>
          <v-card>
            <v-card-title>
              <h3>Agents sur place</h3>
              <v-spacer></v-spacer>
              <v-text-field
                  v-model="fetchData.search"
                  class="mb-2"
                  append-icon="search"
                  label="Recherche (nom - destination)"
                  hide-details
              ></v-text-field>
            </v-card-title>
            <v-data-table
                :loading="fetchData.noFetchYet"
                :headers="fetchData.headers"
                :items="fetchData.agents"
                disable-pagination
                hide-default-footer
                :search="fetchData.search"
                :sort-by="['name']"
                item-key="id"
                no-data-text="Aucun agent sur place ! "
                class="elevation-1 tbl"
            >

              <template #header.name="{ header }">
                <span class="name-header">
                  {{ header.text }}
                </span>
              </template>
              <template #item.name="{ value }">
                <div class="name-cell">
                  {{value}}
                </div>
              </template>
              <template #item.sites="{ value }">
                <div class="sites-cell">
                  <site-label v-for="site in value" :sitename="site.name" :key="site.id" />
                </div>
              </template>
              <template #item.islands="{ value }">
                <div class="islands-cell">
                  <span class="text-sm-caption">
                    {{ value?.map(i => i.name).join(' - ') }}
                  </span>
                </div>

              </template>
              <template #item.startDate="{ value }">
                {{_parseAndFormatDate(value, datePatternConfig.en.localDate, datePatternConfig.fr.localDate) }}
              </template>
              <template #item.stopDate="{ value }">
                {{_parseAndFormatDate(value, datePatternConfig.en.localDate, datePatternConfig.fr.localDate) }}
              </template>
              <template #item.edit="{item}">
                <v-row no-gutters justify="end" class="pr-4">
                  <v-icon small class="mr-2" @click="edit(item)">edit</v-icon>
                  <v-icon small @click="del(item)">delete</v-icon>
                </v-row>
              </template>
            </v-data-table>
          </v-card>
        </v-container>
      </template>

      <!-- EDIT DIALOG -->
      <smart-dialog :title="edition.title" v-model="edition.dialog" :width="632">
        <v-container v-if="edition.agent">
          <v-form v-if="edition.context !== 'Delete'" v-on:submit.prevent="saveAgent"  ref="form" v-model="edition.valid">
            <!-- Name - Row -->
            <p class="font-weight-medium mt-1 mb-2">Informations principales</p>
            <v-row dense class="px-4">
              <v-col cols="8">
                <v-text-field dense  v-model="edition.agent.name" label="Nom"
                               :rules="rules.nonEmptyTrim('Le nom est requis')" />
              </v-col>
            </v-row>

            <!-- startDate + StopDate - Row -->
            <v-row dense class="px-4">
              <v-col class="pr-1" cols="6">
                <smart-picker
                    v-model="startDateFr"
                    :stopDate="stopDateFr"
                    :hideDetails="false"
                    :rules="[...rules.nonEmpty('Date de départ requise'), ...rules.isValidFrDate('Date de départ invalide')]"
                    :inputOpts="{
                label: 'Date de départ',
                clearable: true,
                placeholder: ' ',
              }"
                />
              </v-col>
              <v-col cols="6" class="pr-1">
                <smart-picker
                    v-model="stopDateFr"
                    :startDate="startDateFr"
                    :rules="[...rules.nonEmpty('Date d’arrivée requise'), ...rules.isValidFrDate('Date d’arrivée invalide')]"
                    :hideDetails="false"
                    :inputOpts="{
                label: 'Date d’arrivée',
                clearable: true,
                placeholder: ' ',
              }"
                />
              </v-col>
            </v-row>

            <!-- Destination + Islands + Sites - Row -->
            <v-divider class="mt-4 mb-8"/>
            <v-row v-if="!fetchData.extraLoading" dense class="px-4">
              <v-col cols="12">
                <v-select
                    v-model="edition.agent.destinationId"
                    :items="fetchData.destinations"
                    item-text="name"
                    item-value="id"
                    label="Destination"
                    :rules="rules.nonEmpty('Destination requise')"
                    clearable
                    dense
                    @change="onDestinationChange"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                    v-model="edition.agent.islandIds"
                    :items="filteredIslands"
                    item-text="name"
                    item-value="id"
                    label="Îles"
                    multiple
                    chips
                    clearable
                    dense
                    no-data-text="Pas d'île disponible pour cette destination"
                    :disabled="!edition.agent.destinationId"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                    v-model="edition.agent.siteId"
                    :items="fetchData.sites"
                    item-text="name"
                    item-value="id"
                    label="Sites"
                    multiple
                    chips
                    clearable
                    dense
                />
              </v-col>
            </v-row>
            <v-skeleton-loader v-else type="list-item-avatar-two-line, list-item-three-line" />

            <!-- Translation - Row -->
            <v-divider class="my-4"/>
            <p class="font-weight-medium mb-2">Traductions</p>
            <v-row dense class="px-4">
              <v-col cols="4">
                <v-select
                    :items="langs"
                    dense
                    label="Langue"
                    v-model="selectedLang"
                />
              </v-col>
              <v-col cols="8">
                <v-text-field dense label="Nom" v-model="nameModel" />
              </v-col>
              <v-col cols="12">
                <v-text-field dense label="Téléphone" v-model="telModel" />
              </v-col>
              <v-col cols="12">
                <v-textarea dense :rows="2" auto-grow label="Assistance" v-model="assistanceModel" />
              </v-col>
              <v-col cols="12">
                <v-textarea
                    dense
                    auto-grow
                    :rows="2"
                    label="Policy"
                    v-model="policyModel"
                />
              </v-col>
            </v-row>

            <!-- Address - Row -->
            <v-divider class="my-4"/>
            <p class="font-weight-medium mb-2">Adresse de l'agent sur place</p>
            <v-row dense class="mt-1 px-4">
              <v-col cols="12">
                <v-text-field dense v-model="edition.agent.address.address1" label="Adresse" :rules="rules.nonEmpty('Ligne 1 d’adresse requise')" />
              </v-col>
              <v-col cols="4">
                <v-text-field dense v-model="edition.agent.address.postalcode" label="Code postal" autocomplete="new-password" />
              </v-col>
              <v-col cols="8">
                <v-text-field dense v-model="edition.agent.address.city" label="Ville" :rules="rules.nonEmpty('Ville requise')"/>
              </v-col>
              <v-col cols="6">
                <v-text-field dense v-model="edition.agent.address.country" label="Pays" :rules="rules.nonEmpty('Pays requis')" />
              </v-col>
              <v-col cols="6">
                <v-text-field dense v-model="edition.agent.address.province" label="Province" />
              </v-col>
            </v-row>
          </v-form>
          <div v-else>
            <p><b>Attention</b>, la suppression d'un agent sur place est <b>définitive.</b></p>
          </div>
        </v-container>
        <template #actions>
          <smart-btn :disabled="edition.isDisabled" primary @click.native="edition.btnFn()">
            {{ edition.context !== 'Delete' ? edition.create ? 'Créer' : 'Enregistrer' : 'Supprimer'}}
          </smart-btn>
        </template>
      </smart-dialog>

    </template>
  </layout>
</template>

<script>
import Layout from "@/views/Layout";
import {get, post} from "@/utils/api";
import FormMixin from "@/components/mixins/FormMixin";
import SiteLabel from "@/components/commons/SiteLabel.vue";
import {EventBus} from "@/components/commons/event-bus";


export default {
  name: "OnPlaceAgentEdit",
  mixins: [FormMixin],
  components: { Layout },
  data: (self) => ({
    fetchData: {
      noFetchYet: true,
      extraLoading: false,
      search: "",
      headers: [
        { text: "NOM", value: "name", align: "left" },
        { text: "DESTINATION", value: "destination.name", align: "left" },
        { text: "ÎLES", value: "islands", align: "left", sortable: false },
        { text: "SITES", value: "sites", align: "left" },
        { text: "DATE DE DÉPART", value: "startDate", align: "center", sortable: true,
          sort: (a, b) => self.sortDates(a, b,  self.datePatternConfig.en.localDate) },
        { text: "DATE D'ARRIVÉE", value: "stopDate", align: "center", sortable: true,
          sort: (a, b) => self.sortDates(a, b,  self.datePatternConfig.en.localDate) },
        { text: "ACTIONS", value: "edit", align: "right" },
      ],
      agents: [],
      destinations: null,
      islands: null,
      sites: null,
    },
    edition: {
      agent: null,
      dialog: false,
      create:false,
      valid: true,
      isDisabled:false,
      context:'Edit',
      btnFn:null,
    },
    selectedLang: null,
  }),
  created() {
    this.init();
  },
  watch: {},
  computed: {
    translations() {
      return this.edition.agent?.translations || []
    },

    langs() {
      return [...new Set(this.translations.map(t => t.lang))].filter(Boolean)
    },

    shouldFetchExtra() {
      return !this.fetchData.destinations || !this.fetchData.islands || !this.fetchData.sites
    },

    currentTranslation() {
      if (!this.selectedLang) return null
      return this.translations.find(t => t.lang === this.selectedLang) || null
    },

    filteredIslands() {
      if (!this.edition.agent?.destinationId) return []
      if (!this.fetchData.islands) return []

      return this.fetchData.islands.filter(
          i => i.destinationId === this.edition.agent.destinationId
      )
    },

    startDateFr: {
      get() {
        const iso = this.edition.agent?.startDate
        if (!iso) return null
        return this._parseAndFormatDate(iso, 'yyyy-MM-dd', 'dd/MM/yyyy')
      },
      set(fr) {
        if (!this.edition.agent) return
        this.edition.agent.startDate = fr
            ? this._parseAndFormatDate(fr, 'dd/MM/yyyy', 'yyyy-MM-dd')
            : null

        // [N] si stop < start => reset stop
        if (this.edition.agent.stopDate && this.edition.agent.startDate
            && this.edition.agent.stopDate < this.edition.agent.startDate) {
          this.edition.agent.stopDate = null
        }
      }
    },

    stopDateFr: {
      get() {
        const iso = this.edition.agent?.stopDate
        if (!iso) return null
        return this._parseAndFormatDate(iso, 'yyyy-MM-dd', 'dd/MM/yyyy')
      },
      set(fr) {
        if (!this.edition.agent) return
        this.edition.agent.stopDate = fr
            ? this._parseAndFormatDate(fr, 'dd/MM/yyyy', 'yyyy-MM-dd')
            : null

        // [N] si stop < start => reset start
        if (this.edition.agent.startDate && this.edition.agent.stopDate
            && this.edition.agent.stopDate < this.edition.agent.startDate) {
          this.edition.agent.startDate = null
        }
      }
    },

    assistanceModel: {
      get() { return this.currentTranslation?.assistance || '' },
      set(v) { if (this.currentTranslation) this.currentTranslation.assistance = v }
    },
    nameModel: {
      get() { return this.currentTranslation?.name || '' },
      set(v) { if (this.currentTranslation) this.currentTranslation.name = v }
    },
    telModel: {
      get() { return this.currentTranslation?.tel || '' },
      set(v) { if (this.currentTranslation) this.currentTranslation.tel = v }
    },
    policyModel: {
      get() { return this.currentTranslation?.policy || '' },
      set(v) { if (this.currentTranslation) this.currentTranslation.policy = v }
    },

  },
  methods: {
    init() {
      const url = "/api/on-place-agent";

      get(url).json((r) => {
        this.fetchData.agents = r;
        this.fetchData.noFetchYet = false
        });
    },
    extraFetch() {
      if (!this.shouldFetchExtra) return
      this.fetchData.extraLoading = true

      Promise.all([
        get('/api/search/destinations').json(),
        get('/api/search/sites').json(),
        get('/api/search/islands').json(),
      ])
        .then(([destinations, sites, islands]) => {
          this.fetchData.destinations = destinations
          this.fetchData.sites = sites
          this.fetchData.islands = islands
        })
        .finally(() => {
          this.fetchData.extraLoading = false
        })
    },
    onDestinationChange(newDestinationId) {
      this.edition.agent.islandIds = []
      if (!newDestinationId) return
      // [N] Selection auto si 1 seule île
      const islandsForDestination = this.fetchData.islands.filter(
          i => i.destinationId === Number(newDestinationId)
      )
      if (islandsForDestination.length === 1) {
        this.edition.agent.islandIds = [islandsForDestination[0].id]
      }
    },
    onNewAgent() {
      this.extraFetch()
      this.edition.title = 'Nouvel agent sur place'
      this.edition.agent = {...this.emptyAgent()};
      this.edition.dialog = true;
      this.edition.create = true;
      this.edition.context='Create'
      this.selectedLang = this.edition.agent.translations[0].lang
      this.edition.btnFn = () => this.createAgent()
    },
    del(agent){
      this.edition.title = "Suppression de l'agent sur place"
      this.edition.agent = { ...agent };
      this.edition.dialog = true;
      this.edition.create = false;
      this.edition.context='Delete'
      this.edition.isDisabled = false
      this.edition.btnFn = () => this.remove(agent)
    },
    edit(agent) {
      this.extraFetch()
      this.edition.title = "Modifier l'agent sur place"
      this.edition.agent = { ...agent };
      this.edition.dialog = true;
      this.edition.create = false;
      this.edition.context ='Edit'
      this.edition.isDisabled = false
      this.edition.btnFn = () => this.saveAgent()
      if (agent.translations.length > 0) {
        this.selectedLang = agent.translations[0].lang
      }
    },
    createAgent() {
      if (this.$refs.form.validate() && this.edition.create) {
        const url = `/api/on-place-agent`;

        post(url, this.edition.agent).res(() => {
          this.init();
          this.edition.dialog = false;
          EventBus.$emit("toaster-msg", "Agent sur place créé");
        })
      }
    },
    saveAgent() {
      if (this.$refs.form.validate() && !this.edition.create) {
        const id = this.edition.agent.id
        if (!id) { return }

        const url = `/api/on-place-agent/${id}`;

        post(url, this.edition.agent).res(() => {
          this.init();
          this.edition.dialog = false;
          EventBus.$emit("toaster-msg", "Agent sur place enregistré");
        })
      }
    },
    remove(agent) {
      this.$root
          .$confirm(
              this.$t("confirm"),
              `Confirmez-vous la suppression de l'agent sur place ${agent.name} ?`,
              { width: 350 }
          )
          .then((confirm) => {
            if (!confirm) {
              return Promise.resolve();
            } else {
              return post(`/api/on-place-agent/${agent.id}/remove `)
                  .badRequest((err) => {
                    EventBus.$emit("toaster-msg", err.toString());
                  })
                  .res(() => {
                    this.init();
                    EventBus.$emit("toaster-msg", "Suppression de l'agent sur place confirmée");
                    this.edition.dialog = false
                  });
            }
          });
    },
    emptyAgent() {
      return {
        id: -1,
        name: null,
        addressId: -1,
        address: {
          id: -1,
          address1: null,
          city: null,
          country: null,
          postalcode: null,
          province: null
        },
        destinationId: null,
        destination: null,
        islandsIds: [],
        islands: [],
        siteId: [],
        sites: [],
        startDate: null,
        stopDate: null,
        translations: [
          {
            lang: "fr",
            name: "",
            assistance: "",
            policy: null,
            tel: null,
            telAssistance: null,
          },
          {
            lang: "it",
            name: "",
            assistance: "",
            policy: null,
            tel: null,
            telAssistance: null,
          }
        ]
      }
    },
    sortDates(a, b, pattern) {
      return this._sortDates(a, b, pattern)
    },
  },
};
</script>

<script setup>

</script>

<style scoped>
.islands-cell {
  max-width: 300px;
}

.name-cell, .name-header  {
  padding-left: 8px;
}

.sites-cell {
  max-width: 300px;
  display: flex;
  flex-wrap: wrap;
  gap: 0px 2px;
  padding: 4px 0px;
}
</style>